import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { BASE_URL } from "../API/Constants";
import axios from "../utils/axiosInstance";

const _apiBase = (BASE_URL || "").replace(/\/+$/, "");

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ✅ referral input (editable)
  const [referralCode, setReferralCode] = useState("");

  // ✅ display only blocks (not editable)
  const [refPosition, setRefPosition] = useState("LEFT");
  const [sponsorName, setSponsorName] = useState("");
  const [isFromLink, setIsFromLink] = useState(false);

  // ✅ preview states
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const previewTimer = useRef(null);

  const [profilePic, setProfilePic] = useState(null);
  const previewUrl = useMemo(() => {
    if (!profilePic) return "";
    return URL.createObjectURL(profilePic);
  }, [profilePic]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  // ✅ read query params: ?ref=xxx&pos=LEFT&by=abcd
  useEffect(() => {
    const sp = new URLSearchParams(location.search);

    const ref = (sp.get("ref") || "").trim();
    const pos = (sp.get("pos") || "").trim();
    const by = (sp.get("by") || "").trim();

    if (ref) {
      setReferralCode(ref);
      setIsFromLink(true);
    }
    if (pos) setRefPosition(pos.toUpperCase());
    if (by) setSponsorName(by);
  }, [location.search]);

  // ✅ auto-fetch preview when referralCode + refPosition are ready
  // Do NOT hit the placement API if it's auto-filled from a referral link.
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);

    if (isFromLink) {
      setPreview(null);
      setPreviewErr("");
      return;
    }

    const id = referralCode?.trim();
    if (!id || id.length < 3) {
      setPreview(null);
      setPreviewErr("");
      return;
    }

    previewTimer.current = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        setPreviewErr("");
        const res = await axios.get(
          `${_apiBase}/api/auth/placement-preview?sponsorId=${id}&position=${refPosition || "LEFT"}`
        );
        setPreview(res?.data || null);
      } catch (e) {
        setPreview(null);
        setPreviewErr(
          e?.response?.data?.message ||
          e?.response?.data?.msg ||
          "Could not fetch placement preview."
        );
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => clearTimeout(previewTimer.current);
  }, [referralCode, refPosition, isFromLink]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onPhone = (v) => {
    const digits = String(v || "").replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setProfilePic(null);
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      alert("Max image size 5MB");
      e.target.value = "";
      return;
    }
    setProfilePic(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name || !email || !phone || !password || !confirmPassword) {
      setError("Please fill all required fields");
      return;
    }

    if (phone.length !== 10) {
      setError("Phone number must be exactly 10 digits");
      return;
    }

    if (!referralCode || referralCode.trim().length < 5) {
      setError("Please enter a valid Referral code (at least 5 characters)");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim());
      fd.append("phone", phone.trim());
      fd.append("password", password);
      fd.append("role", "USER");
      fd.append("userType", "TRAINEE_ENTREPRENEUR");

      // ✅ Only send referralCode (as you requested)
      if (referralCode.trim()) fd.append("referralCode", referralCode.trim());
      if (refPosition.trim()) fd.append("position", refPosition.trim());

      // ✅ Do NOT send pos/by (display only)
      if (profilePic) fd.append("profilePic", profilePic);

      const res = await axios.post(`${_apiBase}/api/auth/placement-register`, fd);
      const data = res.data;

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      navigate("/RegisterSuccess", {
        replace: true,
        state: {
          user: data?.user || null,
          placement: data?.placement || null,
          msg: data?.msg || "Registered",
        },
      });
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        html,body,#root{
          height:100%;width:100%;
          font-family:system-ui,-apple-system,Segoe UI,Roboto;
          background:#f6f7fb;color:#111;
        }

        .page{
          min-height:100vh;
          display:flex;
          flex-direction:column;
          justify-content:center;
          align-items:center;
          padding:40px 12px;
          background: linear-gradient(135deg, #004d00 0%, #006600 100%);
        }

        .card{
          width:100%;
          max-width:520px;
          background:#fff;
          border:none;
          border-radius:24px;
          padding:34px 26px;
          box-shadow:0 15px 40px rgba(0,0,0,0.3);
          text-align: center;
        }

        .title{
          font-size:28px;
          font-weight:800;
          color: #111;
          margin-bottom:4px;
        }

        .sub{
          font-size:12px;
          font-weight:800;
          letter-spacing:1px;
          color:#006600;
          margin-bottom:14px;
          text-transform: uppercase;
        }

        .welcome-text {
          font-size: 13px;
          color: #64748b;
          margin-bottom: 24px;
          line-height: 1.6;
          font-weight: 500;
        }

        .field{margin-top:16px; text-align: left;}

        .label{
          font-size:12px;
          font-weight:800;
          margin-bottom:6px;
          color: #475569;
        }

        .input{
          width:100%;
          height: 46px;
          padding:0 14px;
          border-radius:10px;
          border:1.5px solid #dcdfe6;
          outline:none;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .input:focus{
          border-color:#006600;
          box-shadow:0 0 0 3px rgba(0, 102, 0, 0.15);
        }

        .pwdWrap{
          position: relative;
          width: 100%;
        }

        .pwdInput{
          width: 100%;
          height: 46px;
          padding: 0 45px 0 14px;
          border-radius:10px;
          border:1.5px solid #dcdfe6;
          outline:none;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .pwdInput:focus {
          border-color: #006600;
          box-shadow: 0 0 0 3px rgba(0, 102, 0, 0.15);
        }

        .eyeBtn{
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          border:none;
          background:none;
          cursor:pointer;
          font-size:18px;
          color: #94a3b8;
          display: flex;
          align-items: center;
          padding: 5px;
          transition: color 0.1s;
        }

        .eyeBtn:hover {
          color: #006600;
        }

        .btn{
          width:100%;
          margin-top:24px;
          height: 48px;
          border-radius:12px;
          border:none;
          background: linear-gradient(135deg, #004d00 0%, #006600 100%);
          color:#fff;
          font-size: 16px;
          font-weight:800;
          cursor:pointer;
          box-shadow: 0 4px 15px rgba(0, 102, 0, 0.3);
          transition: 0.2s;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 102, 0, 0.4);
        }

        .btn:disabled{opacity:.6; cursor: not-allowed;}

        .error{
          margin-top:16px;
          padding:12px;
          border-radius:10px;
          background:rgba(255,0,0,.05);
          border: 1px solid rgba(255,0,0,0.1);
          color:#b00020;
          font-size:13px;
          font-weight:700;
          text-align: left;
        }

        .links{
          margin-top:20px;
          font-size:14px;
          color: #555;
        }

        .links a{
          font-weight:800;
          color:#006600;
          text-decoration:none;
        }

        .links a:hover {
          text-decoration: underline;
        }

        .infoBox{
          border:1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 12px;
          padding: 10px 12px;
          text-align: left;
        }
        .infoK{ font-size:11px; font-weight:800; color:#64748b; }
        .infoV{ font-size:13px; font-weight:700; color:#1e293b; margin-top:2px; }

        @media(max-width:480px){
          .card{padding:16px;}
        }
      `}</style>

      <main className="page">
        <div className="card">
          <div className="title">Create Account</div>
          <div className="sub">BECOME A DISTRIBUTOR</div>

          <div className="welcome-text">
            Join our community of distributors! <br />
            Start your journey towards a brighter future today.
          </div>

          <form onSubmit={handleSubmit}>
            <div className="field">
              <div className="label">Name *</div>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>

            <div className="field">
              <div className="label">Phone *</div>
              <input className="input" value={phone} onChange={(e) => onPhone(e.target.value)} required />
            </div>

            <div className="field">
              <div className="label">Email *</div>
              <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="field">
              <div className="label">Password *</div>
              <div className="pwdWrap">
                <input
                  type={showPwd ? "text" : "password"}
                  className="pwdInput"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="button" className="eyeBtn" onClick={() => setShowPwd(!showPwd)}>
                  {showPwd ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <div className="field">
              <div className="label">Re-enter Password *</div>
              <div className="pwdWrap">
                <input
                  type={showConfirmPwd ? "text" : "password"}
                  className="pwdInput"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="eyeBtn"
                  onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                >
                  {showConfirmPwd ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            {/* ✅ Referral Code input (editable) */}
            <div className="field">
              <div className="label">Referral Code</div>
              <input
                className="input"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase().trim())}
                placeholder="Enter referral code"
                maxLength={10}
              />
            </div>

            {/* ✅ Position Selector */}
            {!isFromLink && (
              <div className="field">
                <div className="label">Select Position</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setRefPosition("LEFT")}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px',
                      border: refPosition === 'LEFT' ? '2.3px solid #006600' : '1.5px solid #dcdfe6',
                      background: '#fff',
                      fontWeight: 800, cursor: 'pointer', color: refPosition === 'LEFT' ? '#006600' : '#64748b'
                    }}
                  >
                    ◀ LEFT
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefPosition("RIGHT")}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px',
                      border: refPosition === 'RIGHT' ? '2.3px solid #006600' : '1.5px solid #dcdfe6',
                      background: '#fff',
                      fontWeight: 800, cursor: 'pointer', color: refPosition === 'RIGHT' ? '#006600' : '#64748b'
                    }}
                  >
                    RIGHT ▶
                  </button>
                </div>
              </div>
            )}

            {/* ✅ New blocks (NOT editable) */}
            <div className="infoGrid">
              {isFromLink && (
                <>
                  <div className="infoBox">
                    <div className="infoK">Position</div>
                    <div className="infoV">{refPosition || "-"}</div>
                  </div>
                  <div className="infoBox">
                    <div className="infoK">Sponsored Name</div>
                    <div className="infoV">{sponsorName || "-"}</div>
                  </div>
                </>
              )}

              {previewLoading && (
                <div className="infoBox" style={{ gridColumn: '1 / -1' }}>
                  <div className="infoV" style={{ color: '#666' }}>Fetching placement details...</div>
                </div>
              )}
              {previewErr && !previewLoading && (
                <div className="infoBox" style={{ gridColumn: '1 / -1', background: 'rgba(255,0,0,0.05)', borderColor: 'rgba(255,0,0,0.2)' }}>
                  <div className="infoV" style={{ color: '#b00020' }}>{previewErr}</div>
                </div>
              )}
              {preview && !previewLoading && (
                <>
                  <div className="infoBox">
                    <div className="infoK">Sponsor ID</div>
                    <div className="infoV">{preview.sponsorId || "-"}</div>
                  </div>
                  <div className="infoBox">
                    <div className="infoK">Sponsor Name</div>
                    <div className="infoV">{preview.sponsorName || "-"}</div>
                  </div>
                  <div className="infoBox">
                    <div className="infoK">Placement Parent ID</div>
                    <div className="infoV">{preview.placementParentId || "-"}</div>
                  </div>
                  <div className="infoBox">
                    <div className="infoK">Placement Parent Name</div>
                    <div className="infoV">{preview.placementParentName || "-"}</div>
                  </div>
                </>
              )}
            </div>

            {/* <div className="field">
              <div className="label">Profile Picture</div>
              <div className="fileWrap">
                <div className="avatar">
                  {previewUrl ? <img src={previewUrl} alt="preview" /> : (name?.[0] || "U").toUpperCase()}
                </div>
                <label className="fileBtn">
                  Choose
                  <input type="file" hidden onChange={handleFile} />
                </label>
              </div>
            </div> */}

            <button className="btn" disabled={loading}>
              {loading ? "Creating..." : "Register"}
            </button>

            {error && <div className="error">{error}</div>}

            <div className="links">
              Already have account? <Link to="/login">Login</Link>
            </div>
          </form>
        </div>
      </main>
    </>
  );
}